'use client';

// based on https://www.radix-ui.com/primitives/docs/components/radio-group#radio-group

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cx } from 'class-variance-authority';
import styles from './radioCardGroup.module.scss';

const RadioCardGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  const radioCardGroupClass = cx(styles.radioCardGroup, className);
  return (
    <RadioGroupPrimitive.Root
      className={radioCardGroupClass}
      {...props}
      ref={ref}
    />
  );
});
RadioCardGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioCardGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    label: string;
    description?: string;
  }
>(({ className, label, description, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cx(styles.radioCardGroupItem, className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        className={styles.radioCardGroupIndicator}
      />
      <p className={styles.label}>{label}</p>
      {description && <div className={styles.description}>{description}</div>}
    </RadioGroupPrimitive.Item>
  );
});
RadioCardGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioCardGroup, RadioCardGroupItem };

type RadioCardGroupItem = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

type DefaultRadioCardGroupProps<T extends RadioCardGroupItem> =
  React.ComponentProps<typeof RadioCardGroup> & {
    items: T[];
  };

export default function DefaultRadioCardGroup<T extends RadioCardGroupItem>({
  items,
  ...props
}: DefaultRadioCardGroupProps<T>) {
  return (
    <RadioCardGroup {...props}>
      {items.map((item) => (
        <div key={item.value} className={cx(styles.defaultItem)}>
          <div className={styles.defaultItemInner}>
            <RadioCardGroupItem
              value={item.value}
              id={item.value}
              label={item.label}
              description={item.description}
              disabled={item.disabled}
            />
          </div>
        </div>
      ))}
    </RadioCardGroup>
  );
}
